import type * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface ConversationRowProps extends Omit<
  React.ComponentProps<'button'>,
  'style' | 'name' | 'children'
> {
  /* What the conversation is called. */
  name: string;
  /* When it is or was, e.g. "Vandaag" or "10 juni". */
  when: string;
  /* The kind pill, beside the name. */
  badge?: React.ReactNode;
}

/*
 * A conversation in the archive.
 *
 * The name carries the row and the date stays out of its way on the right —
 * the list is read by name, and the month header above already said roughly
 * when. The chevron means the same here as on a meeting row: opening it.
 */
export function ConversationRow({
  name,
  when,
  badge,
  className,
  ...props
}: ConversationRowProps) {
  return (
    <button
      data-slot='conversation-row'
      type='button'
      className={cn(
        'flex w-full cursor-pointer items-center gap-3.5 px-5.5 py-4 text-left transition',
        'hover:bg-fill-faint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
        className,
      )}
      {...props}
    >
      <span className='flex min-w-0 flex-1 items-center gap-2'>
        <span className='truncate text-md font-semibold text-ink'>{name}</span>
        {badge}
      </span>
      <span className='shrink-0 text-xs text-ink-muted'>{when}</span>
      <ChevronRight aria-hidden='true' className='size-3.5 shrink-0 text-ink-muted' />
    </button>
  );
}
